import { useState, useCallback } from 'react';
import { usePortalVersion } from '../../contexts/PortalVersionContext.jsx';
import './portal-version-callout.css';

const CALLOUT_STORAGE_KEY = 'palcare-v2-callout-dismissed';

function isCalloutDismissed() {
    try {
        return localStorage.getItem(CALLOUT_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

function persistDismissal() {
    try {
        localStorage.setItem(CALLOUT_STORAGE_KEY, 'true');
    } catch {
        // silently ignore storage errors
    }
}

/**
 * PortalVersionToggle — switch between V1 (classic) and V2 (modern) portal.
 * Accepts a `variant` prop to style itself for each navigation context.
 *
 * On first login (V1 variant only), shows a tooltip callout + pulsing glow
 * to draw attention to the "Try Portal v2" button.
 *
 * @param {{ variant: 'v1' | 'v2' }} props
 */
const PortalVersionToggle = ({ variant = 'v1' }) => {
    const { portalVersion, togglePortalVersion } = usePortalVersion();
    const [showCallout, setShowCallout] = useState(
        () => variant === 'v1' && !isCalloutDismissed()
    );

    const dismissCallout = useCallback(() => {
        setShowCallout(false);
        persistDismissal();
    }, []);

    const handleToggle = useCallback(() => {
        if (showCallout) {
            dismissCallout();
        }
        togglePortalVersion();
    }, [showCallout, dismissCallout, togglePortalVersion]);

    if (variant === 'v2') {
        return (
            <button
                type="button"
                onClick={togglePortalVersion}
                className="nav2-version-toggle"
                title="Switch to classic portal"
            >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
                Switch to Classic
            </button>
        );
    }

    // V1 variant — pill button for old sidebar
    const shouldShowCallout = showCallout && portalVersion === 'v1';

    return (
        <div className="pvt-wrapper">
            {shouldShowCallout && (
                <div
                    className="pvt-tooltip"
                    role="status"
                    aria-live="polite"
                >
                    <p className="pvt-message">
                        ✨ A faster, modern experience awaits you!
                    </p>
                    <button
                        type="button"
                        className="pvt-dismiss"
                        onClick={dismissCallout}
                        aria-label="Dismiss notification"
                    >
                        &times;
                    </button>
                </div>
            )}
            <button
                type="button"
                onClick={handleToggle}
                className={`btn btn-sm mt-2${shouldShowCallout ? ' pvt-glow' : ''}`}
                style={{
                    background: portalVersion === 'v2' ? 'var(--color-primary)' : 'linear-gradient(135deg, #0081c7 0%, #00a2fa 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '6px 16px',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    width: '100%',
                    transition: 'all 200ms ease',
                }}
                title={portalVersion === 'v2' ? 'Switch to classic portal' : 'Try the new portal design'}
            >
                {portalVersion === 'v2' ? '← Classic View' : '✦ Try Portal v2'}
            </button>
        </div>
    );
};

export default PortalVersionToggle;
